import { COLLECTION_MALLS, type CollectionMall } from '../productCollection/collectionMalls';
import {
  newRowKey,
  type CollectionField,
  type CollectionFieldType,
  type ConsentItem,
  type ProductCollectionConfig,
} from '../productCollection/types';
import type { ProductCollectionByMall } from '../productCollection/ProductCollectionMallTabs';

/**
 * QA-563 추가입력·동의 템플릿 — 4몰(국내·일문·중문·영문) 추가입력 항목·동의 묶음. 상품 등록·수정에서 불러와 몰마다 채운다.
 * 템플릿 이름은 관리자만 본다(상품에는 항목만 복사되고 템플릿과 연결되지 않는다 — 템플릿을 고쳐도 기존 상품은 그대로).
 */
export interface CollectionTemplate {
  templateId: string;
  name: string;
  malls: ProductCollectionByMall;
  /** 코드에 내장된 기본 템플릿(대면·영상통화) — 수정·삭제 불가. */
  builtIn?: boolean;
}

type FieldSpec = [label: string, type: CollectionFieldType, sensitive: boolean, options?: string[]];

const field = ([label, type, sensitive, options = []]: FieldSpec): CollectionField => ({
  key: newRowKey('field'),
  label,
  type,
  required: true,
  options,
  sensitive,
});

const CHANNELS = ['KakaoTalk', 'LINE'];

/** 몰별 라벨 — [이름, 생년월일, 휴대폰번호, 이메일, 국적, 채널, 채널ID]. */
const LABELS: Record<CollectionMall, string[]> = {
  'ko-KR': ['이름', '생년월일 (YYMMDD)', '휴대폰번호', '이메일', '국적', '채널', '채널ID'],
  'en-US': ['Name', 'Date of birth (YYMMDD)', 'Phone Number', 'E-mail', 'Nationality', 'Channel', 'Channel ID'],
  'ja-JP': ['名前', '生年月日 (YYMMDD)', '携帯電話番号', 'メールアドレス', '国籍', 'チャンネル', 'チャンネルID'],
  'zh-CN': ['姓名', '出生日期 (YYMMDD)', '手机号码', '电子邮箱', '国籍', '频道', '频道ID'],
};

const buildMalls = (withChannel: boolean): ProductCollectionByMall =>
  COLLECTION_MALLS.reduce((acc, mall) => {
    const [name, birth, phone, email, nationality, channel, channelId] = LABELS[mall];
    // QA-578 — 기본 템플릿은 [개인정보(동의 필요)]를 모두 해제(운영 요청, 기본 템플릿은 개인이 고칠 수 없다).
    //   필요하면 상품에서 불러온 뒤 항목별로 켠다.
    const specs: FieldSpec[] = [
      [name!, 'TEXT', false],
      [birth!, 'TEXT', false],
      [phone!, 'TEXT', false],
      [email!, 'TEXT', false],
      ...(withChannel
        ? ([
            [channel!, 'SELECT', false, CHANNELS],
            [channelId!, 'TEXT', false],
          ] as FieldSpec[])
        : []),
      [nationality!, 'TEXT', false],
    ];
    acc[mall] = { fields: specs.map(field), consents: [] };
    return acc;
  }, {} as ProductCollectionByMall);

/** QA-563 기본 템플릿 — 대면 이벤트용·영상통화 이벤트용(요청서 항목·순서 그대로). */
export const builtInTemplates = (): CollectionTemplate[] => [
  { templateId: 'builtin-offline', name: '대면 이벤트용 (기본)', malls: buildMalls(false), builtIn: true },
  { templateId: 'builtin-video-call', name: '영상통화 이벤트용 (기본)', malls: buildMalls(true), builtIn: true },
];

const norm = (s: string) => s.trim().toLowerCase();

/**
 * 템플릿을 상품의 몰별 설정 뒤에 붙인다 — 이미 같은 라벨(동의는 제목)이 있는 행은 건너뛴다. 새 행 키를 새로 만든다
 * (같은 템플릿을 두 번 불러와도 중복이 생기지 않고, 붙인 뒤 행 추가·삭제는 평소처럼).
 */
export const appendTemplate = (current: ProductCollectionConfig, tpl: ProductCollectionConfig): ProductCollectionConfig => {
  const labels = new Set(current.fields.map((f) => norm(f.label)));
  const titles = new Set(current.consents.map((c) => norm(c.title)));
  const fields: CollectionField[] = tpl.fields
    .filter((f) => f.label.trim() && !labels.has(norm(f.label)))
    .map((f) => ({ ...f, options: [...f.options], key: newRowKey('field') }));
  const consents: ConsentItem[] = tpl.consents
    .filter((c) => c.title.trim() && !titles.has(norm(c.title)))
    .map((c) => ({ ...c, key: newRowKey('consent') }));
  return { fields: [...current.fields, ...fields], consents: [...current.consents, ...consents] };
};

/** 몰마다 붙였을 때 실제로 더해지는 항목 수(확인 문구용). */
export const countAdded = (current: ProductCollectionByMall, tpl: ProductCollectionByMall): number =>
  COLLECTION_MALLS.reduce((n, m) => {
    const next = appendTemplate(current[m], tpl[m]);
    return n + (next.fields.length - current[m].fields.length) + (next.consents.length - current[m].consents.length);
  }, 0);
